import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Boxes,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Crown,
  Star,
  Zap,
  Lock,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ModuleCard, ModuleConfigModal } from '../../components/modules';
import {
  useTenantModules,
  useUpdateModuleConfig,
} from '../../hooks/useModules';
import {
  PlatformModule,
  PlanTier,
  TenantModuleStatus,
  TIER_LABELS,
} from '../../types/modules';
import { useAuthStore } from '../../stores/auth.store';

export function TenantModules() {
  const { user } = useAuthStore();
  const tenantId = user?.tenantId;

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [configModule, setConfigModule] = useState<TenantModuleStatus | null>(null);

  // Hooks
  const { data: tenantModules, isLoading, refetch } = useTenantModules(tenantId);
  const updateConfig = useUpdateModuleConfig();

  // Get unique categories from enabled modules only
  const categories = tenantModules?.modules
    ? [...new Set(tenantModules.modules.filter(m => m.isEnabled).map(m => m.category))]
    : [];

  // Filter only enabled modules
  const filteredModules = tenantModules?.modules.filter(module => {
    if (!module.isEnabled) return false;
    const matchesSearch = module.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      module.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || module.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }) || [];

  // Group by category
  const modulesByCategory = filteredModules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, TenantModuleStatus[]>);

  // Handlers
  const handleSaveConfig = async (moduleId: string, config: Record<string, any>) => {
    if (!tenantId) return;

    await updateConfig.mutateAsync({
      tenantId,
      moduleId: moduleId as PlatformModule,
      config,
    });
  };

  // Stats
  const enabledCount = tenantModules?.modules.filter(m => m.isEnabled).length || 0;
  const totalCount = tenantModules?.modules.length || 0;

  const getTierIcon = (tier: PlanTier) => {
    switch (tier) {
      case PlanTier.BASIC:
        return <Star className="w-5 h-5" />;
      case PlanTier.STANDARD:
        return <Zap className="w-5 h-5" />;
      case PlanTier.PREMIUM:
        return <Crown className="w-5 h-5" />;
    }
  };

  const getTierColor = (tier: PlanTier) => {
    switch (tier) {
      case PlanTier.BASIC:
        return 'bg-gray-100 text-gray-700';
      case PlanTier.STANDARD:
        return 'bg-blue-100 text-blue-700';
      case PlanTier.PREMIUM:
        return 'bg-amber-100 text-amber-700';
    }
  };

  if (!tenantId) {
    return (
      <Card className="p-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Erro de Autenticação
        </h3>
        <p className="text-gray-500">
          Não foi possível identificar o tenant. Faça login novamente.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Boxes className="w-7 h-7 text-blue-600" />
            Módulos Ativos
          </h1>
          <p className="text-gray-500 mt-1">
            Visualize e configure os módulos habilitados para sua empresa
          </p>
        </div>
        <Button
          onClick={() => refetch()}
          variant="secondary"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* Stats */}
      {tenantModules && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Módulos Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{enabledCount}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Lock className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Módulos Disponíveis</p>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${getTierColor(tenantModules.tier)}`}>
                {getTierIcon(tenantModules.tier)}
              </div>
              <div>
                <p className="text-sm text-gray-500">Seu Plano</p>
                <p className="text-2xl font-bold text-gray-900">
                  {TIER_LABELS[tenantModules.tier]}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Info Banner */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Sobre os Módulos</h3>
            <p className="text-sm text-blue-700 mt-1">
              Os módulos são habilitados pelo administrador da plataforma. 
              Você pode configurar os módulos ativos de acordo com as necessidades da sua empresa.
              Para ativar novos módulos ou fazer upgrade do plano, entre em contato com o suporte.
            </p>
          </div>
        </div>
      </Card>

      {/* Filters */}
      {enabledCount > 0 && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar módulos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Todas as categorias</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
            </div>
          </div>
        </Card>
      )}

      {/* Modules Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : enabledCount === 0 ? (
        <Card className="p-12 text-center">
          <Boxes className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nenhum Módulo Ativo
          </h3>
          <p className="text-gray-500">
            Sua empresa ainda não possui módulos habilitados.
            Entre em contato com o administrador da plataforma para ativar os módulos desejados.
          </p>
        </Card>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredModules.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                tenantTier={tenantModules?.tier || PlanTier.BASIC}
                onToggle={() => {}} // Tenant admin cannot toggle
                onConfigure={setConfigModule}
                isLoading={false}
                canManage={false} // Only configure, not toggle
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Config Modal */}
      <ModuleConfigModal
        module={configModule}
        isOpen={!!configModule}
        onClose={() => setConfigModule(null)}
        onSave={handleSaveConfig}
        isLoading={updateConfig.isPending}
      />
    </div>
  );
}

export default TenantModules;
